import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import sortBy from "lodash/sortBy";
import reject from "lodash/reject";

import Button from "../Buttons/Button";
import Icon from "../Utils/Icon";
import { Desktop, NonDesktop } from "../Utils/ResponsiveQueries";
import { useFormatMessage } from "../Intl/Context";
import { useNavigateTo } from "../Routes/RoutesContext";

const ModalBreadcrumbs = props => {
  const { breadcrumbs, closeModal, onMobileBack } = props;

  const t = useFormatMessage();
  const navigationModalBreadcrumbLabel = t(intlMessages.navigationModalBreadcrumb);

  const navigateTo = useNavigateTo();

  const handleBackArrowClicked = useCallback(
    e => {
      const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];

      if (previousBreadcrumb && previousBreadcrumb.onClick) {
        previousBreadcrumb.onClick(e);
      } else if (previousBreadcrumb && previousBreadcrumb.url) {
        navigateTo(previousBreadcrumb.url);
      } else {
        closeModal(e);
      }
    },
    [breadcrumbs, closeModal, navigateTo]
  );

  const activeModals = useSelector(({ modals }) => modals.activeModals);

  const modals = useMemo(() => {
    const sortedModals = sortBy(activeModals, modal => modal.position);

    return reject(sortedModals, modal => !modal.title);
  }, [activeModals]);

  const title =
    props.title ||
    (breadcrumbs.length >= 2 && breadcrumbs[breadcrumbs.length - 2].title) ||
    null;

  // This avoids having a single-entry ol/li hierarchy, which is not RGAA compliant
  const hasMultipleBreadcrumbEntries = modals.length > 1;

  const NavTag = hasMultipleBreadcrumbEntries ? "nav" : "div";
  const ListTag = hasMultipleBreadcrumbEntries ? "ol" : "div";
  const ListItemTag = hasMultipleBreadcrumbEntries ? "li" : "div";

  const navRole = hasMultipleBreadcrumbEntries ? { role: "navigation" } : {};

  return (
    <NavTag
      className="modal-breadcrumb"
      aria-label={navigationModalBreadcrumbLabel}
      {...navRole}
    >
      <Desktop>
        <ListTag className="breadcrumb">
          {modals.map(modal => {
            const { id, title, isOpen } = modal;
            const ariaCurrentPage = isOpen ? { "aria-current": "page" } : {};

            return (
              <ListItemTag key={id} className="breadcrumb-item" {...ariaCurrentPage}>
                {isOpen && (
                  <span className="breadcrumb-link text-primary-inverse active">
                    {title}
                  </span>
                )}

                {!isOpen && (
                  <Button
                    onClick={closeModal}
                    className="breadcrumb-link text-primary-inverse"
                    label={title}
                  />
                )}
              </ListItemTag>
            );
          })}
        </ListTag>
      </Desktop>

      <NonDesktop>
        <span className="breadcrumb">
          {props.breadcrumbs.length < 3 && (
            <button
              className="breadcrumb-back-arrow"
              onClick={onMobileBack || handleBackArrowClicked}
            >
              <Icon className="icon-arrow-left icon-arrow-left-circle icon-with-shadow text-primary-inverse" />
            </button>
          )}

          <span className="d-block breadcrumb-label">{title}</span>
        </span>
      </NonDesktop>
    </NavTag>
  );
};

const intlMessages = defineMessages({
  navigationModalBreadcrumb: {
    id: "modalBreadcrumbs.navigationModalBreadcrumb",
    defaultMessage: "Vous êtes ici"
  }
});

export default ModalBreadcrumbs;
